import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Box,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Avatar,
  AvatarGroup,
  Stack,
  LinearProgress,
  Badge,
} from '@mui/material';
import {
  MoreVert,
  Schedule as ScheduleIcon,
  PlayArrow,
  Stop,
  Edit,
  ContentCopy,
  Delete,
  Devices,
  PlaylistPlay,
  Visibility,
  Warning,
  Error,
  CheckCircle,
  Pause,
  Repeat,
  AccessTime,
  Person,
  CalendarToday,
  LocationOn,
  Notifications,
  NotificationsOff,
} from '@mui/icons-material';

import { ScheduleStatus, Priority, RecurrenceType, UserRole, ScheduleType } from "../../types/index";
import type { Schedule, ScheduleEvent } from "../../types/index";

interface ScheduleCardProps {
  schedule: Schedule | ScheduleEvent;
  onPlay?: (schedule: Schedule | ScheduleEvent) => void;
  onStop?: (schedule: Schedule | ScheduleEvent) => void;
  onEdit?: (schedule: Schedule | ScheduleEvent) => void;
  onDuplicate?: (schedule: Schedule | ScheduleEvent) => void;
  onDelete?: (schedule: Schedule | ScheduleEvent) => void;
  onViewDetails?: (schedule: Schedule | ScheduleEvent) => void;
  onResolveConflicts?: (schedule: Schedule | ScheduleEvent) => void;
  currentUserRole?: UserRole;
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  schedule,
  onPlay,
  onStop,
  onEdit,
  onDuplicate,
  onDelete,
  onViewDetails,
  onResolveConflicts,
  currentUserRole = UserRole.VIEWER,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  // Определяем, это Schedule или ScheduleEvent
  const isScheduleEvent = 'status' in schedule && 'start_time' in schedule;
  const scheduleEvent = isScheduleEvent ? schedule as ScheduleEvent : null;
  const scheduleItem = schedule as Schedule;

  // Получаем статус (для Schedule используем состояние на основе is_active)
  const getScheduleStatus = (): ScheduleStatus => {
    if (isScheduleEvent && scheduleEvent) {
      return scheduleEvent.status as any; // EventStatus vs ScheduleStatus
    }
    // Для Schedule определяем статус на основе доступных полей
    if (!scheduleItem.is_active) {
      return ScheduleStatus.CANCELLED;
    }
    
    const now = new Date();
    const startDate = new Date(scheduleItem.start_date);
    const endDate = scheduleItem.end_date ? new Date(scheduleItem.end_date) : null;
    
    if (startDate > now) {
      return ScheduleStatus.SCHEDULED;
    }
    if (endDate && endDate < now) {
      return ScheduleStatus.COMPLETED;
    }
    return ScheduleStatus.ACTIVE;
  };

  const status = getScheduleStatus();
  const priority = scheduleEvent?.priority || Priority.NORMAL;

  const getStatusColor = (status: ScheduleStatus) => {
    switch (status) {
      case ScheduleStatus.ACTIVE:
        return 'success';
      case ScheduleStatus.SCHEDULED:
        return 'info';
      case ScheduleStatus.DRAFT:
        return 'warning';
      case ScheduleStatus.COMPLETED:
        return 'success';
      case ScheduleStatus.CANCELLED:
        return 'error';
      case ScheduleStatus.CONFLICT:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: ScheduleStatus) => {
    switch (status) {
      case ScheduleStatus.ACTIVE:
        return <PlayArrow fontSize="small" />;
      case ScheduleStatus.SCHEDULED:
        return <ScheduleIcon fontSize="small" />;
      case ScheduleStatus.DRAFT:
        return <Edit fontSize="small" />;
      case ScheduleStatus.COMPLETED:
        return <CheckCircle fontSize="small" />;
      case ScheduleStatus.CANCELLED:
        return <Stop fontSize="small" />;
      case ScheduleStatus.CONFLICT:
        return <Warning fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENT:
        return 'error';
      case Priority.HIGH:
        return 'warning';
      case Priority.NORMAL:
        return 'info';
      case Priority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (startTime: string, endTime: string): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    }
    return `${minutes}м`;
  };

  const getRecurrenceText = (pattern: any): string => {
    if (!scheduleItem.repeat_config) return '';
    
    // Обработка repeat_config для Schedule
    const config = scheduleItem.repeat_config;
    if (config.type) {
      switch (config.type) {
        case 'daily':
          return config.interval === 1 ? t('schedule.recurrence.daily') : t('schedule.recurrence.everyNDays', { n: config.interval });
        case 'weekly':
          return config.interval === 1 ? t('schedule.recurrence.weekly') : t('schedule.recurrence.everyNWeeks', { n: config.interval });
        case 'monthly':
          return config.interval === 1 ? t('schedule.recurrence.monthly') : t('schedule.recurrence.everyNMonths', { n: config.interval });
        default:
          return t('schedule.recurrence.custom');
      }
    }
    return t('schedule.recurrence.custom');
  };

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMs < 0) {
      const pastDays = Math.abs(diffDays);
      if (pastDays === 0) return t('common.today');
      if (pastDays === 1) return t('common.yesterday');
      return t('common.daysAgo', { count: pastDays });
    }
    
    if (diffDays === 0) {
      if (diffHours === 0) return t('schedule.startsNow');
      return t('schedule.startsInHours', { count: diffHours });
    }
    if (diffDays === 1) return t('common.tomorrow');
    return t('schedule.startsInDays', { count: diffDays });
  };

  const canEdit = () => {
    return [UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER].includes(currentUserRole);
  };

  const canDelete = () => {
    return [UserRole.ADMIN, UserRole.MANAGER].includes(currentUserRole);
  };

  const canControl = () => {
    return [UserRole.ADMIN, UserRole.MANAGER, UserRole.SCHEDULER, UserRole.ENGINEER].includes(currentUserRole);
  };

  const isConflicted = status === ScheduleStatus.CONFLICT;
  const hasConflicts = scheduleEvent?.conflicts && scheduleEvent.conflicts.length > 0;

  // Функция для получения даты/времени в зависимости от типа
  const getStartTime = (): string => {
    if (isScheduleEvent && scheduleEvent) {
      return scheduleEvent.start_time;
    }
    return scheduleItem.start_date;
  };

  const getEndTime = (): string => {
    if (isScheduleEvent && scheduleEvent) {
      return scheduleEvent.end_time;
    }
    return scheduleItem.end_date || scheduleItem.start_date;
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        border: isConflicted ? 2 : 1,
        borderColor: isConflicted ? 'error.main' : 'divider',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      {/* Заголовок карточки */}
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          {/* Аватар с иконкой статуса */}
          <Avatar
            sx={{
              bgcolor: getStatusColor(status),
              width: 48,
              height: 48,
            }}
          >
            {getStatusIcon(status)}
          </Avatar>

          {/* Основная информация */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                }}
              >
                {schedule.name}
              </Typography>
              
              {/* Индикаторы */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {scheduleItem.repeat_config && (
                  <Tooltip title={getRecurrenceText(scheduleItem.repeat_config)}>
                    <Repeat fontSize="small" color="action" />
                  </Tooltip>
                )}
                {hasConflicts && (
                  <Tooltip title={t('schedule.hasConflicts')}>
                    <Badge badgeContent={scheduleEvent?.conflicts?.length} color="error">
                      <Warning fontSize="small" color="error" />
                    </Badge>
                  </Tooltip>
                )}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                icon={getStatusIcon(status)}
                label={t(`schedule.status.${status.toLowerCase()}`)}
                color={getStatusColor(status)}
                size="small"
                variant="outlined"
              />
              <Chip
                label={t(`schedule.priority.${priority.toString().toLowerCase()}`)}
                color={getPriorityColor(priority)}
                size="small"
                variant="filled"
              />
            </Box>

            {schedule.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {schedule.description}
              </Typography>
            )}
          </Box>

          {/* Меню действий */}
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Box>

        {/* Время выполнения */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
            {isScheduleEvent ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTime fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatTime(getStartTime())} - {formatTime(getEndTime())}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatRelativeTime(getStartTime())}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {formatRelativeTime(getStartTime())}
                  {scheduleItem.end_date && ` - ${formatRelativeTime(scheduleItem.end_date)}`}
                </Typography>
              </Box>
            )}
          </Stack>
          
          {isScheduleEvent && (
            <Typography variant="caption" color="text.secondary">
              {t('schedule.duration')}: {formatDuration(getStartTime(), getEndTime())}
            </Typography>
          )}
        </Box>

        {/* Тип расписания для Schedule */}
        {!isScheduleEvent && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {t('schedule.type')}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {t(`schedule.scheduleType.${scheduleItem.schedule_type}`)}
            </Typography>
          </Box>
        )}

        {/* Плейлист */}
        {scheduleEvent?.playlist && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <PlaylistPlay fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {t('schedule.playlist')}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {scheduleEvent.playlist.name}
            </Typography>
          </Box>
        )}

        {/* Устройства */}
        {scheduleEvent?.device && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Devices fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {t('schedule.device')}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {scheduleEvent.device.name}
            </Typography>
          </Box>
        )}

        {/* Конфликты */}
        {hasConflicts && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Warning fontSize="small" color="error" />
              <Typography variant="caption" color="error">
                {t('schedule.conflicts')} ({scheduleEvent?.conflicts?.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {scheduleEvent?.conflicts?.map((conflict, index) => (
                <Chip
                  key={conflict.id || index}
                  label={(conflict as any).conflict_type?.replace('_', ' ') || 'Конфликт'}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Повторение */}
        {scheduleItem.repeat_config && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Repeat fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {getRecurrenceText(scheduleItem.repeat_config)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Информация об авторе и дате */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {(scheduleEvent?.schedule?.creator || (schedule as any).creator)?.full_name || t('common.unknown')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {new Date(schedule.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </CardContent>

      {/* Действия */}
      <CardActions sx={{ mt: 'auto', px: 2, pb: 2 }}>
        <Button
          size="small"
          onClick={() => onViewDetails?.(schedule)}
          startIcon={<Visibility />}
        >
          {t('common.details')}
        </Button>

        {canControl() && status === ScheduleStatus.SCHEDULED && (
          <Button
            size="small"
            onClick={() => onPlay?.(schedule)}
            startIcon={<PlayArrow />}
            color="primary"
          >
            {t('schedule.start')}
          </Button>
        )}

        {canControl() && status === ScheduleStatus.ACTIVE && (
          <Button
            size="small"
            onClick={() => onStop?.(schedule)}
            startIcon={<Stop />}
            color="error"
          >
            {t('schedule.stop')}
          </Button>
        )}

        {hasConflicts && canEdit() && (
          <Button
            size="small"
            onClick={() => onResolveConflicts?.(schedule)}
            startIcon={<Warning />}
            color="warning"
          >
            {t('schedule.resolve')}
          </Button>
        )}
      </CardActions>

      {/* Контекстное меню */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {onViewDetails && (
          <MenuItem onClick={() => handleAction(() => onViewDetails(schedule))}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedule.viewDetails')}</ListItemText>
          </MenuItem>
        )}

        {canEdit() && onEdit && (
          <MenuItem onClick={() => handleAction(() => onEdit(schedule))}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit')}</ListItemText>
          </MenuItem>
        )}

        {canEdit() && onDuplicate && (
          <MenuItem onClick={() => handleAction(() => onDuplicate(schedule))}>
            <ListItemIcon>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedule.duplicate')}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {canControl() && onPlay && status === ScheduleStatus.SCHEDULED && (
          <MenuItem onClick={() => handleAction(() => onPlay(schedule))}>
            <ListItemIcon>
              <PlayArrow fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedule.start')}</ListItemText>
          </MenuItem>
        )}

        {canControl() && onStop && status === ScheduleStatus.ACTIVE && (
          <MenuItem onClick={() => handleAction(() => onStop(schedule))}>
            <ListItemIcon>
              <Stop fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedule.stop')}</ListItemText>
          </MenuItem>
        )}

        {hasConflicts && canEdit() && onResolveConflicts && (
          <MenuItem onClick={() => handleAction(() => onResolveConflicts(schedule))}>
            <ListItemIcon>
              <Warning fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedule.resolveConflicts')}</ListItemText>
          </MenuItem>
        )}

        {canDelete() && onDelete && [
          <Divider key="divider" />,
          <MenuItem key="delete" onClick={() => handleAction(() => onDelete(schedule))}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
        ]}
      </Menu>
    </Card>
  );
};

export default ScheduleCard; 
 
 
 